import React, { useCallback, useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, ChevronDown, SkipBack, SkipForward, Loader2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import markSrc from "@/assets/codex-mark.png";
import { subscribeDucking } from "@/lib/audioDuck";

/**
 * CODEX Turntable — a vinyl record-player styled ambient audio widget.
 *
 * Rendered ONCE at the app root (outside <Routes/>) so playback and the
 * <audio> element persist seamlessly across page navigation. Starts paused
 * (browsers block autoplay); remembers volume + expanded state.
 *
 * Add tracks to the TRACKS array below (files under /public/audio) and they
 * appear in the playlist with ‹ › switch controls — no other changes needed.
 */

// The CODEX turntable playlist (user-provided tracks). Loops as ambient audio.
const TRACKS = [
  { src: "/audio/codex-main-title.mp3", title: "Codex Main Title", catalog: "CDX-OST-33⅓", note: "" },
  { src: "/audio/mason.mp3", title: "Mason", catalog: "CDX-OST-02", note: "" },
  { src: "/audio/cameron.mp3", title: "Cameron", catalog: "CDX-OST-03", note: "" },
  { src: "/audio/silent-cosmos-1.mp3", title: "Silent Cosmos, Pt. I", catalog: "CDX-OST-04", note: "" },
  { src: "/audio/silent-cosmos-2.mp3", title: "Silent Cosmos, Pt. II", catalog: "CDX-OST-05", note: "" },
  { src: "/audio/suspended-harmony-1.mp3", title: "Suspended Harmony, Pt. I", catalog: "CDX-OST-06", note: "" },
  { src: "/audio/suspended-harmony-2.mp3", title: "Suspended Harmony, Pt. II", catalog: "CDX-OST-07", note: "" },
  { src: "/audio/chronos-abyss.mp3", title: "Chronos Abyss", catalog: "CDX-OST-08", note: "" },
  { src: "/audio/suspense-in-the-dark.mp3", title: "Suspense in the Dark", catalog: "CDX-OST-09", note: "" },
];

const LS_VOL = "codex.turntable.volume";
const LS_OPEN = "codex.turntable.open";
const LS_TRACK = "codex.turntable.track";

// While the Archivist speaks, dip the music to this fraction of its volume.
const DUCK_FACTOR = 0.5;

// ---- Vinyl turntable physics ----
const NORMAL_DPS = 360 * (100 / 3) / 60;   // 33⅓ rpm ≈ 200°/s
const MOTOR_K = 4.2;                        // motor pull toward 1× (spin-up/return)
const HOLD_FRICTION = 9;                    // finger held still → decelerate to 0
const MOVE_IDLE_MS = 55;                    // no finger move within this → "holding"
const MAX_RATE = 3;                         // clamp scrub speed (±)
const RATE_TAU = 0.012;                     // audio-rate smoothing (click-free)
// Tonearm sweep (pivot at top-right). Parked off the disc when idle; drops to the
// outer groove at song start and creeps toward the label as the track plays.
const TONEARM_PARK = -40;                   // lifted fully off the record
const TONEARM_EDGE = -15;                   // stylus at the outer groove (start)
const TONEARM_CENTER = -3;                  // stylus near the label (end)
const WORKLET_URL = "/vinyl-processor.js";
const VINYL_SUPPORTED =
  typeof window !== "undefined" &&
  !!(window.AudioContext || window.webkitAudioContext) &&
  typeof AudioWorkletNode !== "undefined";

/** The spinning vinyl + gold label. `size` in px. */
const Vinyl = ({ size, playing, spinRef, jsDriven }) => (
  <div
    className="relative shrink-0"
    style={{ width: size, height: size }}
    data-testid="turntable-vinyl"
  >
    <div
      ref={spinRef}
      className="codex-vinyl codex-vinyl-motion absolute inset-0"
      data-playing={playing ? "true" : "false"}
      style={jsDriven ? { animation: "none", willChange: "transform" } : undefined}
    >
      {/* Center gold label */}
      <div
        className="codex-vinyl-label absolute rounded-full flex flex-col items-center justify-center overflow-hidden"
        style={{
          width: "44%",
          height: "44%",
          left: "28%",
          top: "28%",
        }}
      >
        <img
          src={markSrc}
          alt=""
          aria-hidden
          draggable={false}
          className="select-none object-contain opacity-90"
          style={{ width: "52%", height: "52%" }}
        />
        {size >= 150 && (
          <span
            className="font-mono uppercase text-[7px] tracking-[0.14em] mt-0.5"
            style={{ color: "var(--codex-gold-ink)" }}
          >
            33⅓ RPM
          </span>
        )}
      </div>
      {/* Spindle hole */}
      <div
        className="absolute rounded-full"
        style={{
          width: 6,
          height: 6,
          left: "calc(50% - 3px)",
          top: "calc(50% - 3px)",
          background: "var(--color-background-black)",
          boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.6)",
        }}
      />
    </div>
  </div>
);

// Short turntable sound effects (synthesized, same-origin).
const SFX = {
  scratch: "/audio/sfx/scratch.wav",
  start: "/audio/sfx/record-start.wav",
  stop: "/audio/sfx/record-stop.wav",
};

export const CodexTurntable = () => {
  const audioRef = useRef(null);
  const [open, setOpen] = useState(() => localStorage.getItem(LS_OPEN) === "1");
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(() => {
    const v = parseFloat(localStorage.getItem(LS_VOL));
    return Number.isFinite(v) ? v : 0.55;
  });
  const [muted, setMuted] = useState(false);
  const [ducked, setDucked] = useState(false);
  const duckedPrevRef = useRef(false);
  const [trackIdx, setTrackIdx] = useState(() => {
    const v = parseInt(localStorage.getItem(LS_TRACK), 10);
    return Number.isInteger(v) && v >= 0 && v < TRACKS.length ? v : 0;
  });
  const track = TRACKS[trackIdx];
  const playingRef = useRef(false);
  playingRef.current = playing;

  // ---- Web Audio graph (enables real volume control on iOS + layered SFX) --
  const ctxRef = useRef(null);
  const srcRef = useRef(null);          // MediaElementSource (create once)
  const musicGainRef = useRef(null);
  const sfxGainRef = useRef(null);
  const sfxBufRef = useRef({});         // decoded AudioBuffers
  const sfxRawRef = useRef({});         // prefetched ArrayBuffers

  // ---- Vinyl engine (AudioBuffer + AudioWorklet: reverse / scrub / pitch) ----
  const workletRef = useRef(null);
  const rateParamRef = useRef(null);
  const playParamRef = useRef(null);
  const noiseGainRef = useRef(null);
  const engine = useRef({ ready: false, setup: null, loadedIdx: -1, length: 0, pos: 0, ended: false });
  const endedCbRef = useRef(null);
  const [engineReady, setEngineReady] = useState(false);
  const [cueing, setCueing] = useState(false);
  const loadTokenRef = useRef(0);       // "latest switch wins" guard for async decode
  // Physics (kept in refs so the 60fps loop never triggers React re-renders)
  const spinRef = useRef(null);       // the spinning vinyl DOM (expanded deck)
  const deckRef = useRef(null);       // deck hit-area (for center/angle math)
  const tonearmRef = useRef(null);    // tonearm SVG (JS-driven when engine active)
  const armAngleRef = useRef(TONEARM_PARK);
  const rafRef = useRef(0);
  const omegaRef = useRef(1);         // normalized angular velocity (1 = normal)
  const angleRef = useRef(0);         // accumulated visual degrees
  const lastTickRef = useRef(0);
  const touchRef = useRef(false);
  const lastAngRef = useRef(0);       // last finger angle (deg)
  const lastMoveTsRef = useRef(0);
  const fingerVelRef = useRef(0);     // smoothed finger angular velocity (normalized)
  const volRef = useRef(0);
  const mutedRef = useRef(false);
  volRef.current = volume;
  mutedRef.current = muted;

  // Prefetch the raw SFX bytes early so they can decode instantly on demand.
  useEffect(() => {
    let alive = true;
    Object.entries(SFX).forEach(async ([k, url]) => {
      try {
        const r = await fetch(url);
        const b = await r.arrayBuffer();
        if (alive) sfxRawRef.current[k] = b;
      } catch (_) {
        /* ignore prefetch errors */
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  const decodeSfx = useCallback(async (name) => {
    const ctx = ctxRef.current;
    if (!ctx || sfxBufRef.current[name]) return;
    let raw = sfxRawRef.current[name];
    if (!raw) {
      try {
        const r = await fetch(SFX[name]);
        raw = await r.arrayBuffer();
      } catch (_) {
        return;
      }
    }
    try {
      const buf = await ctx.decodeAudioData(raw.slice(0));
      sfxBufRef.current[name] = buf;
    } catch (_) {
      /* ignore decode errors */
    }
  }, []);

  // Build the AudioWorklet graph: worklet → music gain, plus a subtle vinyl
  // surface-noise source. Resolves true on success, false to use the fallback.
  const setupWorklet = useCallback(async (ctx, dest) => {
    try {
      await ctx.audioWorklet.addModule(WORKLET_URL);
      const node = new AudioWorkletNode(ctx, "vinyl-processor", {
        numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [2],
      });
      node.connect(dest);
      node.port.onmessage = (e) => {
        const d = e.data;
        if (d.type === "pos") {
          engine.current.pos = d.sample;
          if (d.ended && !engine.current.ended) {
            engine.current.ended = true;
            if (endedCbRef.current) endedCbRef.current();
          }
        }
      };
      workletRef.current = node;
      rateParamRef.current = node.parameters.get("rate");
      playParamRef.current = node.parameters.get("playing");
      rateParamRef.current.value = 1;
      playParamRef.current.value = 0;

      // Soft looping vinyl surface noise (dynamically gated in the physics loop).
      const nb = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 2), ctx.sampleRate);
      const ch = nb.getChannelData(0);
      let last = 0;
      for (let i = 0; i < ch.length; i++) {
        const w = Math.random() * 2 - 1;
        last = (last + 0.02 * w) / 1.02;      // gentle low-passed (brownish) noise
        ch[i] = last * 3;
      }
      const nsrc = ctx.createBufferSource();
      nsrc.buffer = nb; nsrc.loop = true;
      const ng = ctx.createGain(); ng.gain.value = 0;
      nsrc.connect(ng).connect(dest); nsrc.start();
      noiseGainRef.current = ng;

      engine.current.ready = true;
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Vinyl worklet unavailable; using basic playback:", e?.message || e);
      engine.current.ready = false;
      return false;
    }
  }, []);

  // Fetch + decode a track into the worklet (channel data is transferred).
  const loadBuffer = useCallback(async (ctx, idx) => {
    const res = await fetch(TRACKS[idx].src);
    const arr = await res.arrayBuffer();
    const audioBuf = await ctx.decodeAudioData(arr);
    const channels = [];
    for (let c = 0; c < audioBuf.numberOfChannels; c++) channels.push(audioBuf.getChannelData(c).slice(0));
    engine.current.length = audioBuf.length;
    engine.current.pos = 0;
    engine.current.ended = false;
    engine.current.loadedIdx = idx;
    workletRef.current.port.postMessage(
      { type: "load", channels, length: audioBuf.length },
      channels.map((c) => c.buffer)
    );
  }, []);

  const setEnginePlaying = useCallback((on) => {
    const p = playParamRef.current, c = ctxRef.current;
    if (!p || !c) return;
    try { p.setValueAtTime(on ? 1 : 0, c.currentTime); } catch (_) { p.value = on ? 1 : 0; }
  }, []);

  // The 60fps platter physics: motor torque, inertia, finger control, and the
  // audio rate + visual rotation + surface-noise that follow from it.
  const startLoop = useCallback(() => {
    if (rafRef.current) return;
    lastTickRef.current = performance.now();
    const tick = (t) => {
      const dt = Math.min(0.05, Math.max(0, (t - lastTickRef.current) / 1000));
      lastTickRef.current = t;
      let w = omegaRef.current;
      const touching = touchRef.current;
      const moving = touching && t - lastMoveTsRef.current <= MOVE_IDLE_MS;

      if (touching && moving) {
        w = fingerVelRef.current;                          // finger drives it
      } else if (touching) {
        w += (0 - w) * Math.min(1, HOLD_FRICTION * dt);    // held still → slows to 0
        fingerVelRef.current = w;
      } else if (playingRef.current) {
        w += (1 - w) * Math.min(1, MOTOR_K * dt);          // motor pulls back to 1×
      } else {
        w += (0 - w) * Math.min(1, MOTOR_K * dt);          // paused → spin down
      }
      if (!touching && !playingRef.current && Math.abs(w) < 0.001) w = 0;
      w = Math.max(-MAX_RATE, Math.min(MAX_RATE, w));
      omegaRef.current = w;

      const ctx = ctxRef.current;
      const rp = rateParamRef.current;
      if (rp && ctx) { try { rp.setTargetAtTime(w, ctx.currentTime, RATE_TAU); } catch (_) { rp.value = w; } }

      // Visual: while the finger is actively moving, the move handler owns rotation
      // (for immediacy); otherwise integrate from angular velocity.
      if (!moving && spinRef.current) {
        angleRef.current = (angleRef.current + w * NORMAL_DPS * dt) % 360;
        spinRef.current.style.transform = `rotate(${angleRef.current}deg)`;
      }

      // Tonearm: parked off the disc when idle; drops to the outer edge on play
      // and eases toward the center as the track's position advances.
      if (tonearmRef.current) {
        const len = engine.current.length || 0;
        const onRecord = (playingRef.current || touching) && len > 1;
        let armTarget = TONEARM_PARK;
        if (onRecord) {
          const prog = Math.max(0, Math.min(1, engine.current.pos / len));
          armTarget = TONEARM_EDGE + (TONEARM_CENTER - TONEARM_EDGE) * prog;
        }
        armAngleRef.current += (armTarget - armAngleRef.current) * Math.min(1, 3 * dt);
        tonearmRef.current.style.transform = `rotate(${armAngleRef.current}deg)`;
      }

      const ng = noiseGainRef.current;
      if (ng && ctx) {
        // Surface/friction "static" removed by request — keep the bus silent so
        // only the record's own audio (slow/stop/reverse) is heard.
        try { ng.gain.setTargetAtTime(0, ctx.currentTime, 0.05); } catch (_) { ng.gain.value = 0; }
      }

      if (playingRef.current || touching || Math.abs(w) > 0.001) rafRef.current = requestAnimationFrame(tick);
      else {
        rafRef.current = 0;
        if (tonearmRef.current) { armAngleRef.current = TONEARM_PARK; tonearmRef.current.style.transform = `rotate(${TONEARM_PARK}deg)`; }
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  // Finger angle around the platter centre (deg).
  const angleOf = useCallback((e) => {
    const el = deckRef.current; if (!el) return 0;
    const r = el.getBoundingClientRect();
    return Math.atan2(e.clientY - (r.top + r.height / 2), e.clientX - (r.left + r.width / 2)) * 180 / Math.PI;
  }, []);

  // Lazily build the audio graph on the first user gesture.
  const ensureGraph = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC || !audioRef.current) return null;
    let ctx;
    try {
      ctx = new AC();
      ctxRef.current = ctx;
      srcRef.current = ctx.createMediaElementSource(audioRef.current);
      const mg = ctx.createGain();
      const sg = ctx.createGain();
      mg.gain.value = (muted ? 0 : volume) * (ducked ? DUCK_FACTOR : 1);
      sg.gain.value = muted ? 0 : 0.3 * volume;
      srcRef.current.connect(mg).connect(ctx.destination);
      sg.connect(ctx.destination);
      musicGainRef.current = mg;
      sfxGainRef.current = sg;
      if (VINYL_SUPPORTED && !engine.current.setup) {
        engine.current.setup = setupWorklet(ctx, mg).then((ok) => { if (ok) setEngineReady(true); return ok; });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Audio graph init failed:", e?.message || e);
    }
    decodeSfx("scratch");
    decodeSfx("start");
    decodeSfx("stop");
    return ctxRef.current;
  }, [muted, volume, ducked, decodeSfx, setupWorklet]);

  const playSfx = useCallback(
    async (name) => {
      const ctx = ctxRef.current;
      const sg = sfxGainRef.current;
      if (!ctx || !sg || muted) return;
      if (!sfxBufRef.current[name]) await decodeSfx(name);
      const buf = sfxBufRef.current[name];
      if (!buf) return;
      try {
        const s = ctx.createBufferSource();
        s.buffer = buf;
        s.connect(sg);
        s.start();
      } catch (_) {
        /* ignore */
      }
    },
    [muted, decodeSfx]
  );

  // Subscribe to the global ducking bus (Archivist voice → dip the music).
  useEffect(() => subscribeDucking(setDucked), []);

  // Apply volume changes via the gain node (works on iOS, unlike el.volume).
  useEffect(() => {
    const ctx = ctxRef.current;
    const target = (muted ? 0 : volume) * (ducked ? DUCK_FACTOR : 1);
    // A duck in/out gets a smooth 2-second fade; direct volume/mute changes glide quickly.
    const duckChanged = duckedPrevRef.current !== ducked;
    duckedPrevRef.current = ducked;
    if (musicGainRef.current && ctx) {
      const g = musicGainRef.current.gain;
      const now = ctx.currentTime;
      try {
        // Hold the current (possibly mid-fade) value, then ramp from there.
        if (typeof g.cancelAndHoldAtTime === "function") g.cancelAndHoldAtTime(now);
        else { g.cancelScheduledValues(now); g.setValueAtTime(g.value, now); }
        if (duckChanged) g.linearRampToValueAtTime(target, now + 2); // 2s fade
        else g.setTargetAtTime(target, now, 0.03);
      } catch (_) {
        g.value = target;
      }
    }
    if (sfxGainRef.current && ctx) {
      const s = muted ? 0 : 0.3 * volume;
      try {
        sfxGainRef.current.gain.setTargetAtTime(s, ctx.currentTime, 0.03);
      } catch (_) {
        sfxGainRef.current.gain.value = s;
      }
    }
    // Fallback ONLY before the Web Audio graph exists — otherwise the gain node
    // owns volume and setting el.volume here would stomp the 2s duck fade.
    if (audioRef.current && !ctx) audioRef.current.volume = target;
    localStorage.setItem(LS_VOL, String(volume));
  }, [volume, muted, ducked]);

  useEffect(() => {
    localStorage.setItem(LS_OPEN, open ? "1" : "0");
  }, [open]);

  const togglePlay = useCallback(async () => {
    const ctx = ensureGraph();
    if (ctx && ctx.state === "suspended") { try { await ctx.resume(); } catch (_) { /* ignore */ } }

    // Preferred path: the vinyl engine (real buffer playback).
    if (ctx && VINYL_SUPPORTED) {
      if (engine.current.setup) { try { await engine.current.setup; } catch (_) { /* ignore */ } }
      if (engine.current.ready) {
        if (!playingRef.current) {
          if (engine.current.loadedIdx !== trackIdx) {
            const token = ++loadTokenRef.current;
            setCueing(true);
            let ok = false;
            try { await loadBuffer(ctx, trackIdx); ok = true; } catch (e) { /* eslint-disable-next-line no-console */ console.warn("decode failed", e); }
            if (token !== loadTokenRef.current) return;   // a newer track switch superseded us
            setCueing(false);
            if (!ok) return;                              // decode failed → don't play the old buffer
          }
          if (engine.current.ended) {
            engine.current.pos = 0; engine.current.ended = false;
            workletRef.current.port.postMessage({ type: "seek", sample: 0 });
          }
          omegaRef.current = 0;             // start from rest → motor winds it up (pitch rises)
          if (rateParamRef.current && ctx) { try { rateParamRef.current.setValueAtTime(0, ctx.currentTime); } catch (_) { rateParamRef.current.value = 0; } }
          setEnginePlaying(true);
          playSfx("start");
          playingRef.current = true;        // update the ref now so the physics loop's first frame sees it
          setPlaying(true);
          startLoop();
        } else {
          setEnginePlaying(false);          // stop advancing (silence held)
          playSfx("stop");
          playingRef.current = false;
          setPlaying(false);
          startLoop();                      // let the platter spin down visually
        }
        return;
      }
    }

    // Fallback: original HTMLAudioElement playback.
    const el = audioRef.current;
    if (!el) return;
    try {
      if (el.paused) { playSfx("start"); const p = el.play(); if (p && typeof p.then === "function") await p; setPlaying(true); }
      else { el.pause(); setPlaying(false); playSfx("stop"); }
    } catch (err) {
      setPlaying(false);
      // eslint-disable-next-line no-console
      console.warn("Turntable playback blocked:", err?.message || err);
    }
  }, [ensureGraph, playSfx, trackIdx, loadBuffer, startLoop, setEnginePlaying]);

  const toggleMute = useCallback(() => setMuted((m) => !m), []);

  // ---- Direct finger control of the vinyl (scratch / spin / hold / reverse) ----
  const onVinylDown = useCallback(async (e) => {
    const ctx = ensureGraph();
    if (ctx && ctx.state === "suspended") { try { await ctx.resume(); } catch (_) { /* ignore */ } }
    // Only the real engine can scrub audio; otherwise just a tactile scratch SFX.
    if (!(ctx && VINYL_SUPPORTED && engine.current.ready && playingRef.current)) return; // engine drives all record audio — no canned SFX
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    touchRef.current = true;
    lastAngRef.current = angleOf(e);
    lastMoveTsRef.current = performance.now();
    fingerVelRef.current = omegaRef.current;
    startLoop();
  }, [ensureGraph, playSfx, angleOf, startLoop]);

  const onVinylMove = useCallback((e) => {
    if (!touchRef.current) return;
    e.preventDefault();
    const now = performance.now();
    const a = angleOf(e);
    let d = a - lastAngRef.current;
    if (d > 180) d -= 360; else if (d < -180) d += 360;   // shortest path across 0/360
    const dt = Math.max(0.001, (now - lastMoveTsRef.current) / 1000);
    const inst = (d / dt) / NORMAL_DPS;                   // finger speed normalized to platter
    fingerVelRef.current = fingerVelRef.current * 0.5 + inst * 0.5;
    omegaRef.current = Math.max(-MAX_RATE, Math.min(MAX_RATE, fingerVelRef.current));
    angleRef.current = (angleRef.current + d) % 360;      // platter tracks the finger exactly
    if (spinRef.current) spinRef.current.style.transform = `rotate(${angleRef.current}deg)`;
    lastAngRef.current = a;
    lastMoveTsRef.current = now;
  }, [angleOf]);

  const onVinylUp = useCallback((e) => {
    if (!touchRef.current) return;
    touchRef.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    omegaRef.current = Math.max(-MAX_RATE, Math.min(MAX_RATE, fingerVelRef.current)); // keep flick momentum
    startLoop();
  }, [startLoop]);

  // Switch tracks in the playlist (wraps around).
  const skipTrack = useCallback((dir) => {
    setTrackIdx((i) => (i + dir + TRACKS.length) % TRACKS.length);
  }, []);

  // When a track finishes, advance to the next one; wrap to the start so the
  // whole playlist loops endlessly. resumeAfterSwitchRef keeps playback going.
  const resumeAfterSwitchRef = useRef(false);
  const handleEnded = useCallback(() => {
    resumeAfterSwitchRef.current = true;
    setTrackIdx((i) => (i + 1) % TRACKS.length);
  }, []);
  // The worklet reports "ended" from the audio thread → advance the same way.
  useEffect(() => { endedCbRef.current = handleEnded; }, [handleEnded]);

  // When the selected track changes, load it (and resume if we were playing,
  // or if we just auto-advanced from the previous track ending).
  const trackMounted = useRef(false);
  useEffect(() => {
    localStorage.setItem(LS_TRACK, String(trackIdx));
    if (!trackMounted.current) { trackMounted.current = true; return; }
    const wantPlay = playingRef.current || resumeAfterSwitchRef.current;

    if (VINYL_SUPPORTED && engine.current.ready && ctxRef.current) {
      const ctx = ctxRef.current;
      (async () => {
        const token = ++loadTokenRef.current;
        setEnginePlaying(false);
        setCueing(true);
        let ok = false;
        try { await loadBuffer(ctx, trackIdx); ok = true; } catch (e) { /* eslint-disable-next-line no-console */ console.warn("track switch decode failed", e); }
        if (token !== loadTokenRef.current) return;      // superseded by a newer switch
        setCueing(false);
        if (!ok) {                                       // decode failed → stop rather than replay the old track
          resumeAfterSwitchRef.current = false;
          playingRef.current = false;
          setPlaying(false);
          return;
        }
        if (wantPlay) {
          resumeAfterSwitchRef.current = false;
          omegaRef.current = 1;             // already spinning → new track at full speed
          setEnginePlaying(true);
          playingRef.current = true;
          setPlaying(true);
          playSfx("start");
          startLoop();
        }
      })();
      return;
    }

    // Fallback: element playback.
    const el = audioRef.current;
    if (!el) return;
    el.load();
    if (wantPlay) {
      resumeAfterSwitchRef.current = false;
      playSfx("start");
      el.play().then(() => setPlaying(true)).catch(() => {});
    }
  }, [trackIdx, playSfx, loadBuffer, startLoop, setEnginePlaying]);

  // Stop the physics loop on unmount.
  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  return (
    <>
      {/* Persistent audio element (never unmounts). */}
      <audio
        ref={audioRef}
        src={track.src}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={handleEnded}
        data-testid="turntable-audio"
      />

      <div className="fixed bottom-14 right-4 z-[200] print:hidden">
        {/* ---------- Collapsed puck ---------- */}
        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open the CODEX turntable"
            data-testid="turntable-open-btn"
            className="group relative flex items-center justify-center rounded-full p-1
                       transition-transform duration-200 hover:scale-105 active:scale-95
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--codex-gold-2)]"
            style={{
              boxShadow: "0 10px 30px rgba(0,0,0,0.5), 0 0 0 1px rgba(216,182,106,0.28)",
              background: "rgba(10,20,34,0.72)",
              backdropFilter: "blur(12px)",
            }}
          >
            <Vinyl size={56} playing={playing} />
            {/* tiny status dot */}
            <span
              className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border"
              style={{
                borderColor: "var(--color-background-black)",
                background: playing ? "var(--color-evidence-well-documented)" : "var(--codex-gold-muted)",
              }}
              aria-hidden
            />
          </button>
        )}

        {/* ---------- Expanded turntable panel ---------- */}
        {open && (
          <div
            className="codex-glass-strong codex-turntable-panel rounded-2xl p-4"
            style={{ width: 288 }}
            data-testid="turntable-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <span className="codex-meta-label" style={{ letterSpacing: "0.24em" }}>
                {playing ? "Now Spinning" : "Turntable"}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Minimize turntable"
                data-testid="turntable-close-btn"
                className="rounded-md p-1 text-[var(--codex-text-muted)] transition-colors duration-150
                           hover:text-[var(--codex-gold-2)] focus-visible:outline-none
                           focus-visible:ring-2 focus-visible:ring-[var(--codex-gold-2)]"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>

            {/* Deck: vinyl (touch to scratch/scrub) + tonearm */}
            <div ref={deckRef} className="relative mx-auto mt-3" style={{ width: 200, height: 200 }}>
              <button
                type="button"
                onPointerDown={onVinylDown}
                onPointerMove={onVinylMove}
                onPointerUp={onVinylUp}
                onPointerCancel={onVinylUp}
                aria-label="Touch and turn the record"
                title="Touch and turn the record — slow it, spin it, or scratch it"
                data-testid="turntable-scratch-btn"
                className="absolute inset-0 rounded-full cursor-grab active:cursor-grabbing
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--codex-gold-2)]"
                style={{ WebkitTapHighlightColor: "transparent", touchAction: "none" }}
              >
                <Vinyl size={200} playing={playing} spinRef={spinRef} jsDriven={engineReady} />
              </button>

              {/* Tonearm (SVG) pivoting from top-right — brushed-chrome tube,
                  gold gimbal, counterweight and a dark headshell with stylus. */}
              <svg
                ref={tonearmRef}
                className="codex-tonearm pointer-events-none absolute inset-0"
                data-playing={playing ? "true" : "false"}
                style={engineReady ? { transition: "none", transformOrigin: "84% 18%" } : undefined}
                width="200"
                height="200"
                viewBox="0 0 200 200"
                fill="none"
                aria-hidden
              >
                <defs>
                  <linearGradient id="ta-tube" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#EEF0F3" />
                    <stop offset="0.32" stopColor="#B4BAC3" />
                    <stop offset="0.5" stopColor="#787E88" />
                    <stop offset="0.68" stopColor="#BCC2CA" />
                    <stop offset="1" stopColor="#666B74" />
                  </linearGradient>
                  <linearGradient id="ta-gold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#F4DE9F" />
                    <stop offset="0.5" stopColor="#D8B66A" />
                    <stop offset="1" stopColor="#8A6B2E" />
                  </linearGradient>
                  <radialGradient id="ta-pivot" cx="0.4" cy="0.34" r="0.85">
                    <stop offset="0" stopColor="#F7F8FA" />
                    <stop offset="0.5" stopColor="#AEB3BB" />
                    <stop offset="1" stopColor="#474B54" />
                  </radialGradient>
                </defs>
                {/* counterweight behind the pivot */}
                <line x1="168" y1="36" x2="189" y2="21" stroke="url(#ta-tube)" strokeWidth="5" strokeLinecap="round" />
                <rect x="178" y="11" width="20" height="21" rx="7" fill="url(#ta-pivot)" stroke="#34383f" strokeWidth="0.8" />
                {/* main tube (soft shadow + brushed chrome + top glint) */}
                <line x1="166" y1="40" x2="96" y2="120" stroke="#22262d" strokeWidth="8" strokeLinecap="round" opacity="0.45" />
                <line x1="166" y1="40" x2="96" y2="120" stroke="url(#ta-tube)" strokeWidth="5.5" strokeLinecap="round" />
                <line x1="167" y1="41" x2="101" y2="115" stroke="rgba(255,255,255,0.6)" strokeWidth="1" strokeLinecap="round" />
                {/* pivot gimbal */}
                <circle cx="168" cy="36" r="11" fill="url(#ta-pivot)" stroke="url(#ta-gold)" strokeWidth="2" />
                <circle cx="168" cy="36" r="3.4" fill="#2b2f37" />
                {/* headshell + cartridge + stylus at the tip */}
                <g transform="rotate(48 96 120)">
                  <rect x="85" y="112" width="21" height="14" rx="3" fill="#15181e" stroke="url(#ta-gold)" strokeWidth="1.2" />
                  <rect x="90" y="126" width="8" height="6" rx="1" fill="#c9ccd2" />
                  <line x1="94" y1="132" x2="94" y2="137.5" stroke="#9aa0a8" strokeWidth="1.4" strokeLinecap="round" />
                </g>
              </svg>
            </div>

            {/* Track info + playlist switcher */}
            <div className="mt-3 flex items-center justify-center gap-2">
              {TRACKS.length > 1 && (
                <button
                  type="button"
                  onClick={() => skipTrack(-1)}
                  disabled={cueing}
                  aria-label="Previous track"
                  data-testid="turntable-prev-track"
                  className="shrink-0 rounded-md p-1 text-[var(--codex-text-muted)] transition-colors duration-150 disabled:opacity-40
                             hover:text-[var(--codex-gold-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--codex-gold-2)]"
                >
                  <SkipBack className="h-4 w-4" fill="currentColor" />
                </button>
              )}
              <div className="min-w-0 text-center">
                <div className="truncate font-serif text-[17px] leading-tight text-[var(--codex-parchment)]" data-testid="turntable-track-title">
                  {track.title}
                </div>
                <div className="codex-archive-id mt-0.5">
                  {track.catalog}
                  {TRACKS.length > 1 ? ` · ${trackIdx + 1}/${TRACKS.length}` : ""}
                </div>
              </div>
              {TRACKS.length > 1 && (
                <button
                  type="button"
                  onClick={() => skipTrack(1)}
                  disabled={cueing}
                  aria-label="Next track"
                  data-testid="turntable-next-track"
                  className="shrink-0 rounded-md p-1 text-[var(--codex-text-muted)] transition-colors duration-150 disabled:opacity-40
                             hover:text-[var(--codex-gold-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--codex-gold-2)]"
                >
                  <SkipForward className="h-4 w-4" fill="currentColor" />
                </button>
              )}
            </div>

            {/* Controls */}
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={togglePlay}
                disabled={cueing}
                aria-label={playing ? "Pause ambient audio" : "Play ambient audio"}
                data-testid="turntable-playpause-btn"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full
                           transition-transform duration-150 hover:scale-105 active:scale-95 disabled:opacity-70
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--codex-gold-2)]"
                style={{
                  background: "linear-gradient(180deg, #F0D896, #D8B66A 55%, #B68A3A)",
                  color: "var(--codex-gold-ink)",
                  boxShadow: "0 6px 18px rgba(216,182,106,0.28)",
                }}
              >
                {cueing ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : playing ? (
                  <Pause className="h-5 w-5" fill="currentColor" />
                ) : (
                  <Play className="h-5 w-5 translate-x-[1px]" fill="currentColor" />
                )}
              </button>

              <button
                type="button"
                onClick={toggleMute}
                aria-label={muted ? "Unmute" : "Mute"}
                data-testid="turntable-mute-btn"
                className="shrink-0 text-[var(--codex-text-muted)] transition-colors duration-150
                           hover:text-[var(--codex-gold-2)] focus-visible:outline-none
                           focus-visible:ring-2 focus-visible:ring-[var(--codex-gold-2)] rounded-md p-1"
              >
                {muted || volume === 0 ? (
                  <VolumeX className="h-5 w-5" />
                ) : (
                  <Volume2 className="h-5 w-5" />
                )}
              </button>

              <Slider
                value={[muted ? 0 : Math.round(volume * 100)]}
                onValueChange={(v) => {
                  setMuted(false);
                  setVolume((v[0] ?? 0) / 100);
                }}
                max={100}
                step={1}
                aria-label="Volume"
                data-testid="turntable-volume-slider"
                className="flex-1"
              />
            </div>

            {/* Optional note (e.g. placeholder) */}
            {track.note ? (
              <div
                className="mt-3 rounded-md px-2 py-1.5 text-center font-mono text-[9px] uppercase tracking-[0.14em]"
                style={{
                  color: "var(--codex-text-faint)",
                  background: "rgba(216,182,106,0.06)",
                  border: "1px solid var(--color-border-subtle)",
                }}
                data-testid="turntable-placeholder-note"
              >
                {track.note}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
};

export default CodexTurntable;
